
import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useInventaire, ArticleInventaire } from '@/hooks/useInventaire';
import ArticleCompactCard from './ArticleCompactCard';
import SubCategoryIcon from './SubCategoryIcon';
import CategoryIcon from './CategoryIcon';
import { useTranslation } from 'react-i18next';

interface VueCompacteCategoriesProps {
  onEdit: (article: ArticleInventaire) => void;
  onDelete?: (id: string) => void;
  showActions?: boolean;
  bateauId?: string;
  onRefresh?: () => void;
  articlesOverride?: ArticleInventaire[];
}

const VueCompacteCategories = ({ 
  onEdit, 
  onDelete, 
  showActions = true,
  bateauId,
  onRefresh,
  articlesOverride
}: VueCompacteCategoriesProps) => {
  const { t } = useTranslation('inventory');
  const { articles: allArticles, modifierQuantite } = useInventaire();
  
  const articlesFiltres = useMemo(() => {
    if (articlesOverride) return articlesOverride;
    return bateauId
      ? allArticles.filter(a => a.bateau_id === bateauId)
      : allArticles;
  }, [allArticles, bateauId, articlesOverride]);

  // Grouper d'abord par catégorie, puis par sous-catégorie
  const articlesParCategorie = useMemo(() => 
    articlesFiltres.reduce((acc, article) => {
      const categorie = article.categorie || t('vueParCategories.noCategory', { defaultValue: 'Sans catégorie' });
      if (!acc[categorie]) {
        acc[categorie] = {};
      }
      
      const sousCategorie = article.sous_categorie || t('vueCompacteCategories.noSubCategory', { defaultValue: 'Sans sous-catégorie' });
      if (!acc[categorie][sousCategorie]) {
        acc[categorie][sousCategorie] = [];
      }
      
      acc[categorie][sousCategorie].push(article);
      return acc;
    }, {} as Record<string, Record<string, ArticleInventaire[]>>),
    [articlesFiltres]
  );

  // États pour contrôler l'affichage des catégories et sous-catégories
  // Initialiser avec une fonction pour calculer les valeurs par défaut une seule fois
  const [collapsedCategories, setCollapsedCategories] = useState<string[]>(() => 
    Object.keys(articlesParCategorie)
  );
  const [collapsedSubCategories, setCollapsedSubCategories] = useState<string[]>(() => {
    const allSubCategoryKeys: string[] = [];
    Object.entries(articlesParCategorie).forEach(([categorie, sousCategories]) => {
      Object.keys(sousCategories).forEach(sousCategorie => {
        allSubCategoryKeys.push(`${categorie}-${sousCategorie}`);
      });
    });
    return allSubCategoryKeys;
  });

  // Ajouter les nouvelles catégories/sous-catégories sans réinitialiser les existantes
  const categoriesKeys = Object.keys(articlesParCategorie);
  React.useEffect(() => {
    // Ajouter seulement les nouvelles catégories (non déjà dans l'état)
    setCollapsedCategories(prev => {
      const newCategories = categoriesKeys.filter(cat => !prev.includes(cat));
      return newCategories.length > 0 ? [...prev, ...newCategories] : prev;
    });
    
    // Ajouter seulement les nouvelles sous-catégories
    const allSubCategoryKeys: string[] = [];
    Object.entries(articlesParCategorie).forEach(([categorie, sousCategories]) => {
      Object.keys(sousCategories).forEach(sousCategorie => {
        allSubCategoryKeys.push(`${categorie}-${sousCategorie}`);
      });
    });
    
    setCollapsedSubCategories(prev => {
      const newSubCategories = allSubCategoryKeys.filter(sub => !prev.includes(sub));
      return newSubCategories.length > 0 ? [...prev, ...newSubCategories] : prev;
    });
  }, [categoriesKeys.join(','), Object.keys(articlesParCategorie).map(k => Object.keys(articlesParCategorie[k]).join(',')).join('|')]);

  const handleQuantityUpdate = async (articleId: string, newQuantity: number) => {
    console.log('Updating article quantity:', articleId, 'to:', newQuantity);
    try {
      // Utiliser la fonction spécialisée modifierQuantite qui ne modifie que la quantité
      const article = articlesFiltres.find(a => a.id === articleId);
      if (!article || !article.bateau_id) {
        throw new Error('Article ou bateau non trouvé');
      }
      
      await modifierQuantite(articleId, newQuantity, article.bateau_id);
      console.log('Article quantity updated successfully');
      // Ne pas appeler onRefresh pour éviter de réinitialiser les catégories ouvertes
    } catch (error) {
      console.error('Failed to update article quantity:', error);
      throw error;
    }
  };

  const toggleCategoryCollapse = (category: string) => {
    setCollapsedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleSubCategoryCollapse = (category: string, subCategory: string) => {
    const key = `${category}-${subCategory}`;
    setCollapsedSubCategories(prev => 
      prev.includes(key)
        ? prev.filter(c => c !== key)
        : [...prev, key]
    );
  };


  if (articlesFiltres.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-nautic" />
            {t('vueCompacteCategories.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            {t('vueCompacteCategories.noArticleFound')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {Object.entries(articlesParCategorie).map(([categorie, sousCategories]) => (
        <div key={categorie} className="space-y-6">
          {/* En-tête de catégorie */}
          <div className="flex items-center justify-between border-b-2 border-nautic pb-3">
            <div className="flex items-center gap-3">
              <CategoryIcon category={categorie} className="h-6 w-6 text-nautic" />
              <h1 className="text-xl font-bold text-nautic">{categorie}</h1>
              <span className="text-sm text-muted-foreground">
                {t('vueCompacteCategories.articlesCount', { count: Object.values(sousCategories).flat().length })}
              </span>
            </div>
            <button
              onClick={() => toggleCategoryCollapse(categorie)}
              className="hover:bg-nautic/10 rounded p-2 transition-colors"
            >
              {collapsedCategories.includes(categorie) ? (
                <ChevronDown className="h-5 w-5 text-nautic" />
              ) : (
                <ChevronUp className="h-5 w-5 text-nautic" />
              )}
            </button>
          </div>

          {/* Sous-catégories */}
          {!collapsedCategories.includes(categorie) && (
            <div className="space-y-6">
              {Object.entries(sousCategories).map(([sousCategorie, articlesCategorie]) => {
                const subCategoryKey = `${categorie}-${sousCategorie}`;
                const isSubCategoryCollapsed = collapsedSubCategories.includes(subCategoryKey);
                
                return (
                  <div key={sousCategorie} className="space-y-4">
                    <div className="flex items-center justify-between border-b border-nautic-light pb-2">
                      <div className="flex items-center gap-2">
                        <SubCategoryIcon 
                          sousCategorie={sousCategorie === t('vueCompacteCategories.noSubCategory', { defaultValue: 'Sans sous-catégorie' }) ? null : sousCategorie} 
                          className="h-4 w-4 text-nautic-light" 
                        />
                        <h2 className="text-lg font-semibold text-nautic-dark">{sousCategorie}</h2>
                        <span className="text-sm text-muted-foreground">{t('vueCompacteCategories.articlesCount', { count: articlesCategorie.length })}</span>
                      </div>
                      <button
                        onClick={() => toggleSubCategoryCollapse(categorie, sousCategorie)}
                        className="hover:bg-nautic/10 rounded p-1 transition-colors"
                      >
                        {isSubCategoryCollapsed ? (
                          <ChevronDown className="h-4 w-4 text-nautic-light" />
                        ) : (
                          <ChevronUp className="h-4 w-4 text-nautic-light" />
                        )}
                      </button>
                    </div>
                    
                    {!isSubCategoryCollapsed && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {articlesCategorie.map((article) => (
                          <ArticleCompactCard
                            key={article.id}
                            article={article}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            showActions={showActions}
                            onQuantityUpdate={handleQuantityUpdate}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default VueCompacteCategories;
